import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DropdownMenu, DropdownItem, UncontrolledDropdown, DropdownToggle, Table, Container, Row, CardHeader, Card, CardFooter, Pagination, PaginationItem, PaginationLink, Button, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter, Collapse,
} from 'reactstrap';
import {
  useReactTable, getCoreRowModel, flexRender, ColumnDef, SortingState, getSortedRowModel,
} from '@tanstack/react-table';
import TimelineHeader from '../../components/headers/TimelineHeader';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import { getPropertiesEndpoint, getMasterPropertiesEndpoint } from './services/propertyService';
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import { PropertiesList } from './models/propertyModel';
import { Paginator } from '../../models/formModel';
import useAuth from '../../hooks/useAuth';
import { useSelector, useDispatch } from 'react-redux';
import { AppStore } from '../../redux/store';
import { setGridFiltersAction } from '../../redux/features/gridSearchBar';
import LoadingCard from '../../components/LoadingCard';
import { generatePagination } from '../../adapters/paginateListAdapter';
import { useNavigate } from 'react-router-dom';
import {
  getCitiesForSearchEndpoint,
  getStatesForSearchEndpoint,
} from '../../services/publicService';
import PropertyMobileListing from './components/PropertyMobileListing';
import useUserCountry from '../../hooks/useUserCountry';
import './css/editable-hover.css';
import { useEditableTable } from './hooks/useEditableTable';
import { CellContext } from '@tanstack/react-table';
import PriceDetailGroupedCell from './components/PropertyOperation/PropertyPriceDetail';
import EditDetailModal from './components/PropertyOperation/EditPricelModal';
import InlineEditCell from './components/PropertyOperation/InlineEditCell';
import PropertyLinksCell from './components/PropertyOperation/PropertyLinksDetail';
import PropertyStatusCell from './components/PropertyOperation/PropertyStatusDetail';
import useUpdatePropertyOperationView from './hooks/useUpdatePropertyOperationView';
import EditOwnerModal from './components/PropertyOperation/EditOwnerModal';
import useUpdateLandlord from '../landlord/hooks/useUpdateLandlord';
import PropertyFeaturesDetail from './components/PropertyOperation/PropertyFeaturesDetail';
import EditFeaturesModal from './components/PropertyOperation/EditFeaturesModal';
import PropertyOwnerDetail from './components/PropertyOperation/PropertyOwnerDetail';
import useCreatePropertyOperation from './hooks/useCreatePropertyOperation';
import useCreateProperty from './hooks/useCreateProperty';
import { getAreaCodesEndpoint } from '../../services/landlordService';
import PropertySupplyDetail from './components/PropertyOperation/PropertySupplyDetail';
import EditSupplyModal from './components/PropertyOperation/EditSupplyModal';
import useAddressLisPicker from '../../hooks/useAddressLisPicker';
import InlineSelectCell from './components/PropertyOperation/InlineSelectCell';
import { InlineMultiSelectCell } from './components/PropertyOperation/InlineMultiSelectCell';
import BrokerGroupedCell from './components/PropertyOperation/PropertyBrokerDetail';
import EditBrokerModal from './components/PropertyOperation/EditBrokerModal';
import PropertyBrokerDetail from './components/PropertyOperation/PropertyBrokerDetail';
import PublishedInLinksCell from './components/PropertyOperation/PublishedInLinksCell';
import PublishedInSection from './components/PropertyOperation/PublishedInSection';
import { set } from 'react-hook-form';
// Estilos constantes
const CARD_CELL_STYLE = {
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 2px 8px 0 rgba(44,62,80,0.06)',
  border: 'none',
  overflow: 'hidden',
  display: 'table-row',
};

const DETAIL_CELL_STYLE: React.CSSProperties = {
  minWidth: 220,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 2,
  position: 'relative',
  paddingTop: 28,
  paddingRight: 32,
};

const EDIT_BUTTON_STYLE = {
  padding: '2px 10px',
  fontSize: 13,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'left',
  gap: 4,
  boxShadow: '0 1px 4px 0 rgba(44,62,80,0.06)',
};

const PropertiesViewOperation = () => {
  // 1. Estado principal
  const [properties, setProperties] = useState<PropertiesList[]>([]);
  const [statesOptions, setStatesOptions] = useState<any[]>([]);
  const [citiesOptions, setCitiesOptions] = useState<any[]>([]);
  const [paginator, setPaginator] = useState<Paginator>({
    page: 0, take: 0, itemCount: 0, pageCount: 0, hasPreviousPage: false, hasNextPage: false,
  });
  const [paginationList, setPaginationList] = useState(Array<number>);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState({
    code: '',
    status: '',
    state_id: '',
    city_id: '',
    source: '',
    zone_of_interest: '',
    start_date: '',
    end_date: '',
    type: '',
    landlord_name: '',
    landlord_email: '',
    landlord_phone: '',
    page: 1,
    take: 10,
  });
  const [areaCodes, setAreaCodes] = useState<any[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [newPropertyZoneOfInterest, setNewPropertyZoneOfInterest] = useState('');

  // 2. Hooks y helpers
  const gridSearchBar = useSelector((store: AppStore) => store.gridSearchBar);
  const { getCurrentCountry } = useUserCountry();
  const currentUserCountryCode = getCurrentCountry()?.code.toLowerCase() ?? 'sv';
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { getAuthToken, isPermissionAvailable, getUserName, getUserEmail } = useAuth();
  const token = getAuthToken();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const defaultBDR = getUserName();

  // 3. Carga de propiedades
  const loadProperties = useCallback(async () => {

    let query = '';
    if (filters.code) query += `code=${filters.code}&`;
    if (filters.status) query += `status=${filters.status}&`;
    if (filters.state_id) query += `state_id=${filters.state_id}&`;
    if (filters.city_id) query += `city_id=${filters.city_id}&`;
    if (filters.source) query += `source=${filters.source}&`;
    if (filters.zone_of_interest) query += `zone_of_interest=${filters.zone_of_interest}&`;
    if (filters.start_date) query += `start_date=${filters.start_date}&`;
    if (filters.end_date) query += `end_date=${filters.end_date}&`;
    if (filters.type) query += `type=${filters.type}&`;
    if (filters.landlord_name) query += `landlord_name=${filters.landlord_name}&`;
    if (filters.landlord_email) query += `landlord_email=${filters.landlord_email}&`;
    if (filters.landlord_phone) query += `landlord_phone=${filters.landlord_phone}&`;
    query += `page=${filters.page}&take=${filters.take}&`;
    query += `sort=desc&`; // <-- Agregado para ordenar descendente


    const endpoint = getMasterPropertiesEndpoint(query, token);
    const { status, data } = await callEndpoint(endpoint);

    if (status === 200) {
      const response = fetchResponseAdapter(data);
      const loadedProperties = response.data.items.map((item: PropertiesList) => {
        let salePrice = item.sale_price ?? null;
        if (!salePrice && item.sale_monthly_price) {
          const match = String(item.sale_monthly_price).replace(/[^0-9.]/g, '');
          salePrice = match ? Number(match) : null;
        }
        const property = {
          ...item,
          landlordName: undefined,
          landlordInfo: item.landlordInfo ?? {},
          id: item.id ?? item.code,
        };

        let publishedInArray: { name: string; url: string }[] = [];
        if (property.published_in) {
          try {
            publishedInArray = JSON.parse(property.published_in);
            if (!Array.isArray(publishedInArray)) throw new Error();
          } catch {
            publishedInArray = property.published_in
              .split(',')
              .map(name => ({ name: name.trim(), url: '' }));
          }
        }

        return property;
      });
      setProperties(loadedProperties);
      setPaginator(response.data.meta);
      setPaginationList(
        generatePagination(
          response.data.meta.page,
          response.data.meta.pageCount,
          5,
        ),
      );
    }
  }, [callEndpoint, filters, token]);

  // 4. Carga de opciones de estados y ciudades
  const loadStatesOptions = useCallback(async () => {
    const { status, data } = await callEndpoint(getStatesForSearchEndpoint());
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      setStatesOptions(
        response.data.map((state: any) => ({
          value: state.state_id,
          label: state.state_name,
        })),
      );
    }
  }, [callEndpoint]);

  const loadCitiesOptions = useCallback(async () => {
    const { status, data } = await callEndpoint(getCitiesForSearchEndpoint());
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      setCitiesOptions(
        response.data.map((city: any) => ({
          value: city.city_id,
          label: city.city_name,
        })),
      );
    }
  }, [callEndpoint]);

  // 4. Carga de códigos de área
  const loadAreaCodes = useCallback(async () => {
    try {
      const { data } = await callEndpoint(getAreaCodesEndpoint(token));

      setAreaCodes(Array.isArray(data.data) ? data['data'] : []);
    } catch {
      setAreaCodes([]);
    }
  }, [callEndpoint]);

  // 5. Efectos de carga inicial SOLO para catálogos
  useEffect(() => {
    loadStatesOptions().catch((e) => console.error(e));
    loadCitiesOptions().catch((e) => console.error(e));
    loadAreaCodes().catch((e) => console.error(e));
  }, []);

  // 5b. Efecto para cargar propiedades cuando cambian los filtros
  useEffect(() => {
    loadProperties().catch((e) => console.error(e));
  }, [filters]);

  // 6. Hook de tabla editable
  const {
    editableProperties,
    setEditableProperties,
  } = useEditableTable<PropertiesList>(properties);

  // 7. Definición de columnas
  const columns = useMemo<ColumnDef<PropertiesList, any>[]>(() => [
    {
      header: 'CODIGO',
      accessorKey: 'code',
      cell: (info: CellContext<PropertiesList, any>) => {

        const broker_id = info.row.original?.broker_id;

        const supplyType = broker_id ? 'BROKER' : 'DIRECTA';
        const supplyColor = broker_id ? '#5e72e4' : '#28a745';


        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 10,
              minWidth: 250,
              width: '100%',
              paddingLeft: 18,
            }}
          >
            <div
              style={{
                background: '#f4f6fa',
                borderRadius: 8,
                boxShadow: '0 1px 4px 0 rgba(44,62,80,0.04)',
                padding: '6px 14px',
                fontWeight: 700,
                color: '#5e72e4',
                fontSize: 16,
                letterSpacing: 1,
                marginBottom: 4,
                minWidth: 60,
                textAlign: 'left',
                transition: 'background 0.2s',
                border: '1px solid #e9ecef',
                display: 'inline-block',
              }}
            >
              <a
                key={`a-code-${info.row.original.id}`}
                href={`/properties/admin/${info.row.original.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#5e72e4',
                  textDecoration: 'none',
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: 1,
                }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >
                {info.getValue() as string}
              </a>
              {/* Supply badge */}
              {supplyType && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: '2px 8px',
                    borderRadius: 8,
                    background: supplyColor,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {supplyType}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#343a40' }}>Nombre:</span>
              <InlineEditCell
                type="text"
                value={info.row.original.name || ''}
                placeholderText='...sin nombre'
                onSave={(nuevoValor: string) => {
                  setEditableProperties(props =>
                    props.map(p =>
                      p.id === info.row.original.id
                        ? { ...p, name: nuevoValor }
                        : p
                    )
                  );
                  handleSavePropertyField(info.row.original.id, 'name', nuevoValor);
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#6c757d' }}>BDR:</span>
              <InlineSelectCell
                value={info.row.original.created_by || defaultBDR || ''}
                options={[
                  ...(catalogs?.bdrs?.map(bdr => ({ value: bdr, label: bdr })) ?? []),
                ]}
                placeholderText="...sin BDR"
                onSave={(nuevoValor: string) => {
                  setEditableProperties(props =>
                    props.map(p =>
                      p.id === info.row.original.id
                        ? { ...p, created_by: nuevoValor }
                        : p
                    )
                  );
                  handleSavePropertyField(info.row.original.id, 'created_by', nuevoValor);
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#6c757d' }}>Canal:</span>
              <InlineSelectCell
                value={info.row.original.source || ''}
                options={
                  (catalogs?.sources ?? []).map((source: string) => ({
                    value: source,
                    label: source,
                  }))
                }
                placeholderText="...sin canal"
                onSave={(nuevoValor: string) => {
                  setEditableProperties(props =>
                    props.map(p =>
                      p.id === info.row.original.id
                        ? { ...p, source: nuevoValor }
                        : p
                    )
                  );
                  handleSavePropertyField(info.row.original.id, 'source', nuevoValor);
                }}
              />
            </div>
        
          </div>
        );
      },
      filterFn: 'includesString',
      meta: {
        thStyle: { minWidth: 250, maxWidth: 300, width: 250, textAlign: 'left' },
        tdStyle: { minWidth: 250, maxWidth: 300, width: 250, textAlign: 'left' },
      },
    },
    {
      id: 'supply',
      header: 'PUBLICACION',
      cell: (info: CellContext<PropertiesList, any>) => {
        const property = info.row.original;
        const broker_id = property?.broker_id;
        const source = property?.source;
        const supplyType = broker_id ? 'BROKER' : 'DIRECTA';
        const supplyColor = broker_id ? '#5e72e4' : '#28a745';

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 10,
              minWidth: 250,
              width: '100%',
              paddingLeft: 18,
            }}
          >          
            {/* Componente PublishedInSection */}
            <PublishedInSection
              property={property}
              setEditableProperties={setEditableProperties}
              handleSavePropertyField={handleSavePropertyField}
            />
          </div>
        );
      },
      meta: {
        thStyle: { minWidth: 100, maxWidth: 160, width: 120, textAlign: 'center' },
        tdStyle: { minWidth: 100, maxWidth: 160, width: 120, textAlign: 'center' },
      },
    },
    {
      id: 'enlaces',
      header: 'Enlaces',
      cell: (info) => (
        <PropertyLinksCell
          property={info.row.original}
          currentUserCountryCode={currentUserCountryCode}
        />
      ),
      meta: {
        thStyle: { minWidth: 180, maxWidth: 260, width: 200, textAlign: 'center' },
        tdStyle: { minWidth: 180, maxWidth: 260, width: 200, textAlign: 'center' },
      },
    },
    {
      id: 'estado',
      header: 'Estado',
      cell: (info) => (
        <PropertyStatusCell property={info.row.original} />
      ),
      meta: {
        thStyle: { minWidth: 120, maxWidth: 160, width: 140, textAlign: 'center' },
        tdStyle: { minWidth: 120, maxWidth: 160, width: 140, textAlign: 'center' },
      },
    },
    {
      id: 'detalle-agrupado',
      header: 'PRECIOS',
      cell: (info) => (
        <PriceDetailGroupedCell
          property={info.row.original}
          editableProperties={editableProperties}
          setDetailProperty={setDetailProperty}
          setDetailModalOpen={setDetailModalOpen}
          currentUserCountryCode={currentUserCountryCode}
          DETAIL_CELL_STYLE={DETAIL_CELL_STYLE}
          EDIT_BUTTON_STYLE={EDIT_BUTTON_STYLE}
          currency={info.row.original.currency}
        />
      ),
    },
    {
      id: 'propietario',
      header: 'PROPIETARIO / BROKER',
      cell: (info) => (
        <>
          <PropertyOwnerDetail
            property={info.row.original}
            setOwnerProperty={setOwnerProperty}
            setOwnerModalOpen={setOwnerModalOpen}
          />
          <br />
          <PropertyBrokerDetail
            property={info.row.original}
            setBrokerProperty={setBrokerProperty}
            setBrokerModalOpen={setBrokerModalOpen}
          />
        </>
      ),
      meta: {
        thStyle: { minWidth: 180, maxWidth: 260, width: 200, textAlign: 'center' },
        tdStyle: { minWidth: 180, maxWidth: 260, width: 200, textAlign: 'center' },
      },
    },
    {
      id: 'direccion',
      header: 'DIRECCIÓN',
      cell: (info) => {
        type AddressRelatives = {
          district_name?: string;
          city_name?: string;
          state_name?: string;
          nb_name?: string;
        };
        const rel: AddressRelatives = info.row.original.address_relatives || {};
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>
              <b>Distrito:</b> {rel.district_name || 'Sin distrito'}
            </span>
            <span>
              <b>Municipio:</b> {rel.city_name || 'Sin municipio'}
            </span>
            <span>
              <b>Departamento:</b> {rel.state_name || 'Sin departamento'}
            </span>
            <span>
              <b>Colonia:</b> {rel.nb_name || 'Sin colonia'}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#6c757d' }}>Zona de interés:</span>
              <InlineSelectCell
                value={info.row.original.zone_of_interest || ''}
                options={
                  (catalogs?.zones ?? []).map((zone: string) => ({
                    value: zone,
                    label: zone,
                  }))
                }
                placeholderText="Selecciona zona"
                onSave={nuevoValor => {
                  setEditableProperties(props =>
                    props.map(p =>
                      p.id === info.row.original.id
                        ? { ...p, zone_of_interest: nuevoValor }
                        : p
                    )
                  );
                  handleSavePropertyField(info.row.original.id, 'zone_of_interest', nuevoValor);
                }}
              />
            </div>
          </div>
        );
      },
      meta: {
        thStyle: { minWidth: 160, maxWidth: 260, width: 200, textAlign: 'center' },
        tdStyle: { minWidth: 160, maxWidth: 260, width: 200, textAlign: 'center' },
      },
    },
    {
      id: 'caracteristicas',
      header: 'CARACTERISTICAS',
      cell: (info) => (
        <PropertyFeaturesDetail
          property={info.row.original}
          setFeaturesProperty={setFeaturesProperty}
          setFeaturesModalOpen={setFeaturesModalOpen}
          EDIT_BUTTON_STYLE={EDIT_BUTTON_STYLE}
        />
      ),
      meta: {
        thStyle: { minWidth: 140, maxWidth: 220, width: 180, textAlign: 'center' },
        tdStyle: { minWidth: 140, maxWidth: 220, width: 180, textAlign: 'center' },
      },
    },
    {
      id: 'property',
      header: () => (
        <span style={{
          fontWeight: 700,
          letterSpacing: 1,
          color: '#6c757d',
          fontSize: 13,
          background: '#f4f6fa',
          borderRadius: 4,
          padding: '2px 8px'
        }}>
          COMPLETADO
        </span>
      ),
      cell: (info: CellContext<PropertiesList, any>) => {
        const progress = info.row.original.admin_data_progress;
        const property = info.row.original;
        const imageUrl =
          property.cover && !property.cover.endsWith('null')
            ? property.cover
            : 'https://storage.googleapis.com/assets-us-east4-propilatam-dev/properties/default/default_1.jpg';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <img
              alt="cover"
              width={175}
              height={100}
              src={imageUrl}
              style={{ borderRadius: 8, objectFit: 'cover', background: '#f4f6fa' }}
            />
            <div style={{
              display: 'inline-block',
              minWidth: 60,
              background: progress === 100 ? '#e6f9f0' : '#f8f9fa',
              color: progress === 100 ? '#28a745' : '#6c757d',
              borderRadius: 6,
              padding: '2px 10px',
              fontWeight: 600,
              fontSize: 13,
              textAlign: 'center',
              border: progress === 100 ? '1px solid #28a74533' : '1px solid #e3e3e3',
              marginTop: 8
            }}>
              {progress}%
              {progress === 100 && (
                <i className="fas fa-check-circle" style={{ color: '#28a745', marginLeft: 6, fontSize: 14 }} />
              )}
            </div>
          </div>
        );
      },
      meta: {
        thStyle: { minWidth: 140, maxWidth: 220, width: 180, textAlign: 'center' },
        tdStyle: { minWidth: 140, maxWidth: 220, width: 180, textAlign: 'center' },
      },
    },
    {
      id: 'actions-dropdown',
      header: '',
      cell: (info: CellContext<PropertiesList, any>) => {
        const property = info.row.original;
        return (
          <div className="text-right">
            <UncontrolledDropdown>
              <DropdownToggle
                className="btn-icon-only text-light"
                href="#"
                role="button"
                size="sm"
                color=""
                onClick={(e) => e.preventDefault()}
              >
                <i className="fas fa-ellipsis-v" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-arrow" right>
                <DropdownItem href={`/properties/admin/${property.id}`}>
                  Ver detalle
                </DropdownItem>
                <DropdownItem href={`/properties/admin/${property.id}/rent`}>
                  Configurar información de renta
                </DropdownItem>
                <DropdownItem href={`/properties/admin/${property.id}/sales`}>
                  Configurar información de venta
                </DropdownItem>
                {isPermissionAvailable('SHOW_MARKETPLACE_SYNC') && (
                  <DropdownItem href={`sync-marketplace/${property.id}/property`}>
                    Sincronizar con marketplace
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        );
      },
    },
  ], [
    filters, editableProperties, setEditableProperties, isPermissionAvailable, currentUserCountryCode
  ]);

  // 9. Configuración de la tabla
  const table = useReactTable({
    data: editableProperties,
    columns,
    getRowId: row => row.id,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // 10. Vista móvil
  const mobileViewComponent = (
    <Row className="mx-2 d-lg-none">
      {properties.length > 0 &&
        properties.map((property) => (
          <PropertyMobileListing key={property.id} property={property} />
        ))}
    </Row>
  );

  // 11. Vista escritorio
  const desktopViewComponent = (
    <div className="d-none d-lg-block">
      <Table
        className="align-items-center table-flush table-sm"
        responsive
        style={{ background: 'transparent', borderSpacing: '0 16px' }}
      >
        <thead className="thead-light">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{ cursor: header.column.getCanSort() ? 'pointer' : undefined }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getIsSorted() === 'asc' && ' 🔼'}
                  {header.column.getIsSorted() === 'desc' && ' 🔽'}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr style={CARD_CELL_STYLE}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      padding: cell.column.id === 'detalle-agrupado'
                        ? '32px 32px 18px 16px'
                        : '18px 16px',
                      verticalAlign: 'middle',
                      border: 'none',
                      background: 'transparent',
                      ...(cell.column.id === 'code'
                        ? { minWidth: 200, maxWidth: 300, width: 200, textAlign: 'left' } // <-- alineación a la izquierda
                        : {}),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              <tr>
                <td colSpan={row.getVisibleCells().length} style={{ height: 16, background: 'transparent', border: 'none', padding: 0 }} />
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );

  // 12. Estado y lógica para el modal de detalle
  const [detalleModalOpen, setDetailModalOpen] = useState(false);
  const [detalleProperty, setDetailProperty] = useState<PropertiesList | null>(null);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [ownerProperty, setOwnerProperty] = useState<PropertiesList | null>(null);

  // Estado para el modal de agregar propiedad
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyBDR, setNewPropertyBDR] = useState('');
  const [newPropertyTypeId, setNewPropertyTypeId] = useState('');
  const [newPropertySource, setNewPropertySource] = useState('');
  const [adding, setAdding] = useState(false);

  // Estado para el modal de características
  const [featuresModalOpen, setFeaturesModalOpen] = useState(false);
  const [featuresProperty, setFeaturesProperty] = useState<PropertiesList | null>(null);

  // Estado para el modal de supply
  const [supplyModalOpen, setSupplyModalOpen] = useState(false);
  const [supplyProperty, setSupplyProperty] = useState<PropertiesList | null>(null);

  // Estado para el modal de broker
  const [brokerModalOpen, setBrokerModalOpen] = useState(false);
  const [brokerProperty, setBrokerProperty] = useState<PropertiesList | null>(null);

  // 13. Función genérica para guardar cambios de una propiedad en el backend usando el hook
  const { updateProperty } = useUpdatePropertyOperationView();
  const { updateLandlord } = useUpdateLandlord();

  const handleSavePropertyField = async (propertyId: string, field: string, value: any) => {
    try {
      await updateProperty(propertyId, { [field]: value });
    } catch (error) {
      //console.error('Error actualizando propiedad:', error);e
    }
  };

  // Función para agregar propiedad usando el hook
  const { createProperty } = useCreatePropertyOperation();

  const { catalogs } = useCreateProperty();

  const handleAddProperty = async () => {
    if (!newPropertyName.trim() || !newPropertyTypeId.trim()) return;
    setAdding(true);
    try {
      const result = await createProperty({
        name: newPropertyName,
        type_id: newPropertyTypeId,
        created_by: newPropertyBDR,
        source: newPropertySource,
        zone_of_interest: newPropertyZoneOfInterest,
      });
      if (result.status === 200 || result.status === 201) {
        await loadProperties();
        setAddModalOpen(false);
        setNewPropertyName('');
        setNewPropertyBDR(defaultBDR || '');
        setNewPropertySource('');
        setNewPropertyTypeId('');
        setNewPropertyZoneOfInterest('');
      } else {
        alert('Error al agregar propiedad');
      }
    } catch (e) {
      alert('Error al agregar propiedad');
    } finally {
      setAdding(false);
    }
  };

  // Limpia el estado al cerrar el modal
  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setNewPropertyName('');
    setNewPropertyTypeId('');
    setNewPropertyBDR(defaultBDR || '');
    setNewPropertySource('');
    setNewPropertyZoneOfInterest('');
  };

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    setNewPropertyName('');
    setNewPropertyTypeId('');
    setNewPropertyBDR(defaultBDR || ''); // <-- Selecciona el valor por defecto
  };

  // 14. Render principal
  const {
    addressLevelListsForView,
    setOptionSelected,
  } = useAddressLisPicker({});

  useEffect(() => {
    let addressFilters: any = {};
    addressLevelListsForView.forEach(level => {
      if (level.selected?.value && level.level_config?.admin_label) {
        if (level.level_config.admin_label === 'Departamento') {
          addressFilters.state_id = level.selected.value;
        }
        if (level.level_config.admin_label === 'Municipio') {
          addressFilters.city_id = level.selected.value;
        }
      }
    });
    setFilters(f => ({
      ...f,
      ...addressFilters,
      page: 1, // <-- reinicia la página
    }));
  }, [addressLevelListsForView]);

  if (!properties && !statesOptions && !citiesOptions && isLoading) {
    return (
      <LoadingCard name={'Lista de propiedades'} parent_name={'Propiedades'} />
    );
  }

  return (
    <>
      <TimelineHeader name="Lista de propiedades" parentName="Propiedades" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <Button
                  color="primary"
                  style={{
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 15,
                    padding: '8px 22px',
                    boxShadow: '0 2px 8px 0 rgba(44,62,80,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'linear-gradient(90deg, #5e72e4 60%, #825ee4 100%)',
                    border: 'none',
                  }}
                  onClick={handleOpenAddModal}
                >
                  <i className="fas fa-plus" style={{ fontSize: 16, marginRight: 8 }} />
                  Agregar propiedad
                </Button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Button
                    color="secondary"
                    outline
                    style={{
                      borderRadius: 8,
                      fontWeight: 600,
                      fontSize: 13,
                      padding: '6px 18px',
                      boxShadow: 'none',
                    }}
                    onClick={() => setFilters({
                      code: '',
                      status: '',
                      state_id: '',
                      city_id: '',
                      source: '',
                      zone_of_interest: '',
                      start_date: '',
                      end_date: '',
                      type: '',
                      landlord_name: '',
                      landlord_email: '',
                      landlord_phone: '',
                      page: 1,
                      take: 10,
                    })}
                  >
                    <i className="fas fa-eraser" style={{ marginRight: 6 }} />
                    Limpiar filtros
                  </Button>
                  <Button
                    color="link"
                    style={{
                      fontWeight: 600,
                      fontSize: 15,
                      color: '#5e72e4',
                      textDecoration: 'none',
                    }}
                    onClick={() => setFiltersOpen(open => !open)}
                  >
                    {filtersOpen ? (
                      <>
                        <i className="fas fa-chevron-up" style={{ marginRight: 6 }} />
                        Ocultar filtros
                      </>
                    ) : (
                      <>
                        <i className="fas fa-chevron-down" style={{ marginRight: 6 }} />
                        Mostrar filtros
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>

              {/* Collapse de filtros */}
              <Collapse isOpen={filtersOpen}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 12,
                    marginBottom: 24,
                    background: '#f4f6fa',
                    padding: '16px 24px',
                    borderRadius: 12,
                    fontSize: 13, // fuente más pequeña
                  }}
                >
                  {/* Filtros principales */}
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-code" style={{ fontWeight: 500, fontSize: 13 }}>Código</Label>
                    <Input
                      id="filter-code"
                      type="text"
                      value={filters.code}
                      onChange={e => setFilters(f => ({ ...f, code: e.target.value, page: 1 }))}
                      placeholder="Código"
                      style={{ fontSize: 13 }}
                    />
                  </FormGroup>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-source" style={{ fontWeight: 500, fontSize: 13 }}>Canal</Label>
                    <Input
                      id="filter-source"
                      type="select"
                      value={filters.source || ''}
                      onChange={e => setFilters(f => ({ ...f, source: e.target.value, page: 1 }))}
                      style={{ fontSize: 13 }}
                    >
                      <option value="">Todos</option>
                      {catalogs?.sources?.map((source: string) => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </Input>
                  </FormGroup>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-state" style={{ fontWeight: 500, fontSize: 13 }}>Zonificación</Label>
                    <Input
                      id="filter-state"
                      type="select"
                      value={filters.zone_of_interest}
                      onChange={e => setFilters(f => ({ ...f, zone_of_interest: e.target.value, page: 1 }))}
                      style={{ fontSize: 13 }}
                    >
                      <option value="">Todos</option>
                      {catalogs?.zones?.map((zone: string) => (
                        <option key={zone} value={zone}>{zone}</option>
                      ))}
                    </Input>
                  </FormGroup>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-start-date" style={{ fontWeight: 500, fontSize: 13 }}>Inicio</Label>
                    <Input
                      id="filter-start-date"
                      type="date"
                      value={filters.start_date || ''}
                      onChange={e => setFilters(f => ({ ...f, start_date: e.target.value, page: 1 }))}
                      style={{ fontSize: 13 }}
                    />
                  </FormGroup>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-end-date" style={{ fontWeight: 500, fontSize: 13 }}>Fin</Label>
                    <Input
                      id="filter-end-date"
                      type="date"
                      value={filters.end_date || ''}
                      onChange={e => setFilters(f => ({ ...f, end_date: e.target.value, page: 1 }))}
                      style={{ fontSize: 13 }}
                    />
                  </FormGroup>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-type" style={{ fontWeight: 500, fontSize: 13 }}>Tipo</Label>
                    <Input
                      id="filter-type"
                      type="select"
                      value={filters.type || ''}
                      onChange={e => setFilters(f => ({ ...f, type: e.target.value, page: 1 }))}
                      style={{ fontSize: 13 }}
                    >
                      <option value="">Todos</option>
                      <option value="for_rent">Renta</option>
                      <option value="for_sale">Venta</option>
                    </Input>
                  </FormGroup>

                  {/* Filtros de propietario */}
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-landlord-name" style={{ fontWeight: 500, fontSize: 13 }}>Nombre propietario</Label>
                    <Input
                      id="filter-landlord-name"
                      type="text"
                      value={filters.landlord_name || ''}
                      onChange={e => setFilters(f => ({ ...f, landlord_name: e.target.value, page: 1 }))}
                      placeholder="Nombre"
                      style={{ fontSize: 13 }}
                    />
                  </FormGroup>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-landlord-email" style={{ fontWeight: 500, fontSize: 13 }}>Email propietario</Label>
                    <Input
                      id="filter-landlord-email"
                      type="text"
                      value={filters.landlord_email || ''}
                      onChange={e => setFilters(f => ({ ...f, landlord_email: e.target.value, page: 1 }))}
                      placeholder="Email"
                      style={{ fontSize: 13 }}
                    />
                  </FormGroup>
                  <FormGroup style={{ marginBottom: 0 }}>
                    <Label for="filter-landlord-phone" style={{ fontWeight: 500, fontSize: 13 }}>Teléfono propietario</Label>
                    <Input
                      id="filter-landlord-phone"
                      type="text"
                      value={filters.landlord_phone || ''}
                      onChange={e => setFilters(f => ({ ...f, landlord_phone: e.target.value, page: 1 }))}
                      placeholder="Teléfono"
                      style={{ fontSize: 13 }}
                    />
                  </FormGroup>

                  {/* Ubicación dinámica */}
                  {addressLevelListsForView.map((level, idx) => (
                    <FormGroup key={idx} style={{ marginBottom: 0 }}>
                      <Label style={{ fontWeight: 500, fontSize: 13 }}>
                        {level.level_config?.admin_label}
                      </Label>
                      <Input
                        type="select"
                        value={level.selected?.value || ''}
                        onChange={e => {
                          const value = isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value);
                          const selectedOption = level.list.find(opt => opt.value === value);
                          if (selectedOption) {
                            setOptionSelected({
                              selected_option: selectedOption,
                              search_level: (level.level_config?.level ?? 0) + 1,
                              assignable: level.level_config?.assignable ?? false,
                            });
                            setFilters(f => ({ ...f, page: 1 }));
                          }
                        }}
                        style={{ fontSize: 13 }}
                      >
                        <option value="">Todos</option>
                        {level.list.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  ))}

                  {/* Botón filtrar */}
                  <div style={{ gridColumn: '1/-1', textAlign: 'right', marginTop: 8 }}>
                    <Button
                      color="primary"
                      style={{ borderRadius: 8, fontWeight: 600, fontSize: 13, padding: '6px 18px' }}
                      onClick={() => loadProperties()}
                    >
                      Filtrar
                    </Button>
                  </div>
                </div>
              </Collapse>

              {/*  Vista escritorio */}

              {/*  Vista escritorio */}
              {isLoading ? (
                <div style={{ width: '100%', textAlign: 'center', padding: '48px 0' }}>
                  <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#5e72e4' }} />
                  <div style={{ marginTop: 12, fontWeight: 600, color: '#5e72e4' }}>Cargando propiedades...</div>
                </div>
              ) : (
                <>
                  {desktopViewComponent}
                  {mobileViewComponent}
                </>
              )}
              <CardFooter className="py-4">
                <nav aria-label="...">
                  <Pagination
                    className="pagination justify-content-end mb-0"
                    listClassName="justify-content-end mb-0"
                  >
                    <PaginationItem
                      className={!paginator.hasPreviousPage ? 'disabled' : ''}
                    >
                      <PaginationLink
                        onClick={() => {
                          setFilters(f => ({
                            ...f,
                            page: paginator.page - 1,
                            take: paginator.take,
                          }));
                        }}
                      >
                        <i className="fas fa-angle-left" />
                        <span className="sr-only">Anterior</span>
                      </PaginationLink>
                    </PaginationItem>
                    {paginationList.map((page) => (
                      <PaginationItem
                        key={page}
                        className={paginator.page === page ? 'active' : ''}
                      >
                        <PaginationLink
                          onClick={() => {
                            setFilters(f => ({
                              ...f,
                              page: page, // actualiza la página
                              take: paginator.take,
                            }));
                          }}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem
                      className={!paginator.hasNextPage ? 'disabled' : ''}
                    >
                      <PaginationLink
                        onClick={() => {
                          setFilters(f => ({
                            ...f,
                            page: paginator.page + 1,
                            take: paginator.take,
                          }));
                        }}
                      >
                        <i className="fas fa-angle-right" />
                        <span className="sr-only">Siguiente</span>
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </nav>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Modal para agregar propiedad */}
      <Modal isOpen={addModalOpen} toggle={handleCloseAddModal} centered>
        <ModalHeader toggle={handleCloseAddModal}>
          Agregar nueva propiedad
        </ModalHeader>
        <ModalBody>
          <Input
            type="text"
            placeholder="Nombre de la propiedad"
            value={newPropertyName}
            onChange={e => setNewPropertyName(e.target.value)}
            autoFocus
            disabled={adding}
            className="mb-3"
            required
          />
          <Input
            type="select"
            placeholder="BDR"
            value={newPropertyBDR}
            onChange={e => setNewPropertyBDR(e.target.value)}
            disabled={adding}
            className="mb-3"
            required
          >
            <option value="">...sin BDR</option>
            {catalogs?.bdrs?.map((bdr: string) => (
              <option key={bdr} value={bdr}>{bdr}</option>
            ))}
          </Input>
          <Input
            type="select"
            placeholder="Canal"
            value={newPropertySource}
            onChange={e => setNewPropertySource(e.target.value)}
            disabled={adding}
            className="mb-3"
            required
          >
            <option value="">...sin canal</option>
            {catalogs?.sources?.map((source: string) => (
              <option key={source} value={source}>{source}</option>
            ))}
          </Input>
          <Input
            type="select"
            value={newPropertyTypeId}
            onChange={e => setNewPropertyTypeId(e.target.value)}
            disabled={adding}
            required
          >
            <option value="">Selecciona tipo de propiedad</option>
            {catalogs?.propertyTypes?.map((type: { id: string; type: string }) => (
              <option key={type.id} value={type.id}>
                {type.type}
              </option>
            ))}
          </Input>
          <Input
            type="select"
            placeholder="Zona de interés"
            value={newPropertyZoneOfInterest}
            onChange={e => setNewPropertyZoneOfInterest(e.target.value)}
            disabled={adding}
            className="mb-3"
            required
          >
            <option value="">Selecciona zona de interés</option>
            {catalogs?.zones?.map((zone: string) => (
              <option key={zone} value={zone}>{zone}</option>
            ))}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseAddModal} disabled={adding}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleAddProperty}
            disabled={
              adding ||
              !newPropertyName.trim() ||
              !newPropertyBDR.trim() ||
              !newPropertySource.trim() ||
              !newPropertyTypeId.trim() ||
              !newPropertyZoneOfInterest.trim()
            }
          >
            {adding ? 'Agregando...' : 'Agregar'}
          </Button>
        </ModalFooter>
      </Modal>

      <EditDetailModal
        isOpen={detalleModalOpen}
        toggle={() => setDetailModalOpen(false)}
        detalleProperty={detalleProperty}
        setDetalleProperty={setDetailProperty}
        onSave={async () => {
          if (!detalleProperty) return;
          try {
            await updateProperty(detalleProperty.id, {
              sale_price: detalleProperty.sale_price === null ? undefined : detalleProperty.sale_price,
              sale_min_price: detalleProperty.sale_min_price === null ? undefined : detalleProperty.sale_min_price,
              price: detalleProperty.price === null ? undefined : detalleProperty.price,
              min_price: detalleProperty.offer_min_price === null ? undefined : detalleProperty.offer_min_price,
            });
            setEditableProperties(props =>
              props.map(p =>
                p.id === detalleProperty.id
                  ? {
                    ...p,
                    sale_price: detalleProperty.sale_price,
                    sale_min_price: detalleProperty.sale_min_price,
                    price: detalleProperty.price,
                    offer_min_price: detalleProperty.offer_min_price,
                  }
                  : p
              )
            );
            setDetailModalOpen(false);
          } catch (e) {
            alert('Error al guardar los cambios');
          }
        }}
      />

      <EditOwnerModal
        isOpen={ownerModalOpen}
        toggle={() => setOwnerModalOpen(false)}
        ownerProperty={ownerProperty}
        setOwnerProperty={setOwnerProperty}
        onSave={async (editableInfo) => {
          if (!ownerProperty) return;
          try {
            /*
            const response = await updateLandlord(ownerProperty.landlord_id, {
              phone: editableInfo.phone,
              first_name: editableInfo.first_name,
              last_name: editableInfo.last_name,
              email: editableInfo.email,
              area_code: editableInfo.area_code,
              username: editableInfo.email
            });
            

            //console.log('Respuesta de updateLandlord:', response);

            const lid =
              response &&
                response.data &&
                response.data.data &&
                response.data.data.lid
                ? response.data.data.lid
                : null;
                */
            const lid = ownerProperty.landlord_id;
            if (lid) {
              setOwnerProperty({
                ...ownerProperty,
                landlord_id: lid,
                landlordInfo: {
                  ...ownerProperty.landlordInfo,
                  ...editableInfo,
                },
              });
              await updateProperty(ownerProperty.id, {
                landlord_id: lid
              });
            }

            setEditableProperties(props =>
              props.map(p =>
                p.id === ownerProperty.id
                  ? {
                    ...p,
                    landlord_id: lid || p.landlord_id,
                    landlordInfo: {
                      ...p.landlordInfo,
                      ...editableInfo,
                    },
                    phone: editableInfo.phone,
                  }
                  : p
              )
            );
            setOwnerModalOpen(false);
            await loadProperties();
          } catch (e) {
            alert('Error al guardar propietario');
          }
        }}
        areaCodes={areaCodes}
      />

      {featuresProperty && (
        <EditFeaturesModal
          isOpen={featuresModalOpen}
          toggle={() => setFeaturesModalOpen(false)}
          property={featuresProperty}
          setProperty={setFeaturesProperty}
          onSave={async (updatedFeatures) => {
            if (!featuresProperty) return;
            try {
              await updateProperty(featuresProperty.id, {
                is_furnished: updatedFeatures.is_furnished,
                pets_allowed: updatedFeatures.pets_allowed,
                has_white_goods: updatedFeatures.has_white_goods,
                accept_airbnb: updatedFeatures.accept_airbnb,
                with_assessment: updatedFeatures.with_assessment,
                is_featured: updatedFeatures.is_featured,
              });
              setEditableProperties(props =>
                props.map(p =>
                  p.id === featuresProperty.id
                    ? { ...p, ...updatedFeatures }
                    : p
                )
              );
              setFeaturesModalOpen(false);
              await loadProperties();
            } catch (e) {
              alert('Error al guardar características');
            }
          }}
        />
      )}

      {supplyProperty && (
        <EditSupplyModal
          isOpen={supplyModalOpen}
          toggle={() => setSupplyModalOpen(false)}
          property={supplyProperty}
          onSave={async (newSupply: { broker_id: string; source: string }) => {
            if (!supplyProperty) return;
            try {
              await updateProperty(supplyProperty.id, {
                //broker_id: newSupply.broker_id,
                source: newSupply.source,
              });
              setEditableProperties(props =>
                props.map(p =>
                  p.id === supplyProperty.id
                    ? { ...p, broker_id: newSupply.broker_id, source: newSupply.source }
                    : p
                )
              );
              setSupplyModalOpen(false);
              await loadProperties();
            } catch (e) {
              alert('Error al guardar supply');
            }
          }}
        />
      )}

      <EditBrokerModal
        isOpen={brokerModalOpen}
        toggle={() => setBrokerModalOpen(false)}
        brokerProperty={brokerProperty}
        setBrokerProperty={setBrokerProperty}
        onSave={async (editableInfo) => {
          if (!brokerProperty) return;
          try {
            await updateProperty(brokerProperty.id, {
              broker_id: editableInfo.broker_id,
            });
            setEditableProperties(props =>
              props.map(p =>
                p.id === brokerProperty.id
                  ? {
                    ...p,
                    broker_id: editableInfo.broker_id,
                    brokerInfo: {
                      ...p.brokerInfo,
                      ...editableInfo,
                    },
                  }
                  : p
              )
            );
            setBrokerModalOpen(false);
            await loadProperties();
          } catch (e) {
            alert('Error al guardar broker');
          }
        }}
        areaCodes={areaCodes}
      />
    </>
  );
};

export default PropertiesViewOperation;